import { useCallback, useEffect, useState } from "react";
import { MarketType } from "../../../Utils";

/**
 * Custom hook for managing prediction scores state
 * @param {Array} filteredMatches - Array of filtered matches
 * @param {Array} userPredictions - Array of user predictions
 * @returns {Object} - Score state and handlers
 */
export const usePredictionScores = (filteredMatches, userPredictions) => {
	const [currentScores, setCurrentScores] = useState({});
	const [isButtonActive, setIsButtonActive] = useState(false);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [hasChangedSinceSubmission, setHasChangedSinceSubmission] = useState(false);

	// Initialize scores for all matches when they change
	useEffect(() => {
		const scores = {};
		filteredMatches.forEach((match) => {
			// Check if user already has a prediction for this match
			const existingPrediction = userPredictions.find((pred) => pred.matchId === match.id && pred.market === MarketType.CORRECT_SCORE_ADVANCED);

			if (existingPrediction) {
				const [home, away] = existingPrediction.prediction.value.split(":");
				scores[match.id] = { homeTeamScore: Number(home), awayTeamScore: Number(away) };
			} else {
				scores[match.id] = { homeTeamScore: 0, awayTeamScore: 0 };
			}
		});
		setCurrentScores(scores);
	}, [filteredMatches, userPredictions]);

	/**
	 * Handle score changes for a specific match
	 * @param {string} matchId - The match ID
	 * @param {Object} score - The new score object
	 */
	const handleScoreChange = useCallback(
		(matchId, score) => {
			// Update the score for this match
			setCurrentScores((prev) => ({
				...prev,
				[matchId]: score,
			}));

			// Activate button on first interaction
			if (!isButtonActive) {
				setIsButtonActive(true);
			}

			// Mark that changes have been made since last submission
			if (hasSubmitted) {
				setHasChangedSinceSubmission(true);
			}
		},
		[isButtonActive, hasSubmitted],
	);

	/**
	 * Reset submission state after successful submission
	 */
	const handleSuccessfulSubmission = useCallback(() => {
		setHasSubmitted(true);
		setHasChangedSinceSubmission(false);
	}, []);

	return {
		currentScores,
		isButtonActive,
		hasSubmitted,
		hasChangedSinceSubmission,
		setHasSubmitted,
		handleScoreChange,
		handleSuccessfulSubmission,
	};
};
